// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useId, useRef, useState } from 'react';

/**
 * Renders a Mermaid diagram from source the LLM emitted in a fenced
 * ```mermaid block. Mermaid is loaded lazily — the ~70 KB bundle is
 * only pulled in when the first diagram appears in a reply, so the
 * default advisor-ui critical path is unaffected.
 *
 * Security:
 *   - `securityLevel: 'strict'` (Mermaid's safest mode) blocks any
 *     HTML in diagram labels, so a prompt-injected diagram can't
 *     inject scripts.
 *   - Source length is capped at 4 kB in the parent FundChatbot
 *     interception layer (see R-3 in the PRD risk register).
 *
 * Failure modes:
 *   - Parse error → render the source verbatim in a red-bordered
 *     pre block so the advisor sees what Fin tried to emit.
 */
export interface MermaidDiagramProps {
  /** Raw Mermaid source from the fenced ```mermaid block. */
  source: string;
}

let mermaidInitialised = false;

export function MermaidDiagram({ source }: MermaidDiagramProps) {
  const id = useId().replace(/[^a-zA-Z0-9-]/g, '');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);

    (async () => {
      try {
        const mermaid = (await import('mermaid')).default;
        if (!mermaidInitialised) {
          mermaid.initialize({
            startOnLoad: false,
            // 'strict' is the safest mode — no HTML in labels, no
            // foreignObject tags. Necessary because diagram source is
            // LLM-emitted user content.
            securityLevel: 'strict',
            theme: 'neutral',
            fontFamily: 'inherit',
          });
          mermaidInitialised = true;
        }
        const { svg } = await mermaid.render(`mermaid-${id}`, source);
        if (cancelled) return;
        if (containerRef.current) {
          containerRef.current.innerHTML = svg;
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message ?? 'Mermaid render failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, source]);

  if (error) {
    return (
      <pre
        data-testid='mermaid-diagram-parse-error'
        style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: 8,
                 borderRadius: 6, fontSize: 12, overflow: 'auto', margin: '4px 0' }}
      >
        {`Diagram did not parse (${error})\n\n${source}`}
      </pre>
    );
  }

  return (
    <div
      ref={containerRef}
      className='fund-chatbot-mermaid-diagram'
      data-testid='fund-chatbot-mermaid-diagram'
      style={{ margin: '8px 0', maxWidth: '100%', overflow: 'auto' }}
    />
  );
}
